import React, { useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popover,
  Switch,
  Typography,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { V4_ICON_BUTTON } from '../../theme/v4Compat';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FilterListOutlined } from '@mui/icons-material';
import { AnimalActivity, RoomChartProps } from './types';
import { ChartHelpers, usePrevious } from './chart-helpers';
import ActivityLegend from './ActivityLegend';
import ApexCharts, { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import _ from 'lodash';
import { VitalityTrackerService } from '../../hooks/VitalityTrackerService';
import { AnimalGroupAnalyticsResult, FloorPlanAreaType } from '../../models/vitality-tracker';
import { tokens } from '../../theme/tokens';

const TotalChart: React.FC<RoomChartProps> = (props) => {
  const styles = sxStyles(useTheme());
  const vitalityTrackerService = new VitalityTrackerService();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [data, setData] = useState<AnimalGroupAnalyticsResult>();
  const [isAvgCount, setIsAvgCount] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [date, setDate] = React.useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });

  useMemo(() => {
    if (date.start && date.end) {
      setIsLoading(true);
      vitalityTrackerService
        .getGroupAnalytics(
          props.locations.map((x) => x.id),
          date.start,
          date.end,
          isAvgCount
        )
        .then((result) => {
          setData(result);
        })
        .finally(() => setIsLoading(false));
    }
  }, [date, isAvgCount]);

  useEffect(() => {
    setChart({
      series: chart.series,
      options: {
        ...chart.options,
        noData: {
          ...chart.options.noData,
          text: isLoading ? 'Loading...' : undefined
        }
      }
    });
  }, [isLoading]);

  useEffect(() => {
    const filteredData = data?.data.filter(
      (entry) => props.locations.find((location) => location.id === entry.locationId)?.isSelected
    );
    // Set series
    const allItems = filteredData?.map((entry) => entry.activities.map((value) => value)).flat();
    // Get distinct activities and map them to colors
    if (!allItems) return;
    const activitiesWithColors = ChartHelpers.getActivitiesWithColors();
    const series = _.uniqBy(allItems, 'type').map((item) => {
      return {
        name: FloorPlanAreaType[item.type],
        data: filteredData?.map((entry) => {
          return entry.activities.find((value) => value.type === item.type)?.value || 0;
        }),
        color: activitiesWithColors.find((activity) => activity.value === item.type.toString())?.color
      };
    });
    // Set X axis
    const rooms = filteredData?.map((entry) => entry.locationName);

    // Set max Y axis value to the highest sum of all values of each room
    // @ts-ignore
    const max = Math.max(
      ...(filteredData?.map((entry) => {
        return entry.activities.map((value) => value.value).reduce((a, b) => a + b, 0);
      }) || [])
    );

    setChart({
      series: series,
      options: {
        ...chart.options,
        xaxis: { ...chart.options.xaxis, categories: rooms },
        yaxis: { ...chart.options.yaxis, max: max }
      }
    });
    setActivities(activitiesWithColors);
  }, [data, props.locations]);

  const [activities, setActivities] = React.useState<AnimalActivity[]>([]);
  const [chart, setChart] = useState<{ series: any; options: ApexOptions }>({
    series: [],
    options: {
      noData: {
        text: undefined,
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: 0,
        style: {
          fontFamily: tokens.fontFamily
        }
      },
      chart: {
        id: ChartHelpers.getUuid(),
        type: 'bar',
        height: 500,
        stacked: true,
        fontFamily: tokens.fontFamily,
        toolbar: {
          show: false
        },
        animations: {
          enabled: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          dataLabels: {
            total: {
              enabled: false
            }
          }
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val: string | number) {
          return isAvgCount ? val : val + 'h';
        }
      },
      yaxis: {
        title: {
          text: undefined
        },
        show: false
      },
      fill: {
        opacity: 1
      },
      tooltip: {},
      legend: {
        show: false
      },
      colors: [
        // @ts-ignore
        function ({ value, seriesIndex, w }) {
          const activitiesWithColors = ChartHelpers.getActivitiesWithColors();
          return activitiesWithColors.find((activity) => activity.key === w.globals.seriesNames[seriesIndex])?.color;
        }
      ]
    }
  });

  const setStartDate = (value: unknown) => {
    const startDate = value as Date | null;
    setDate({ ...date, start: startDate });
  };

  const setEndDate = (value: unknown) => {
    const endDate = value as Date | null;
    setDate({ ...date, end: endDate });
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const prevActivities = usePrevious(activities);

  useEffect(() => {
    if (prevActivities?.length === 0) return;
    const chartId = chart.options.chart?.id;
    if (chartId) {
      activities?.forEach((activity) => {
        if (
          activity.isSelected !==
          prevActivities?.find((prevActivity) => prevActivity.value === activity.value)?.isSelected
        ) {
          ApexCharts.exec(chartId, activity.isSelected ? 'showSeries' : 'hideSeries', activity.value);
        }
      });
    }
  }, [activities]);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsAvgCount(event.target.checked);
    const chartId = chart.options.chart?.id;
    if (chartId) {
      ApexCharts.exec(chartId, 'updateOptions', {
        dataLabels: {
          formatter: function (val: string | number, _opts: any, event: any) {
            event.persist();
            return event.target.checked ? val : val + 'h';
          }
        }
      });
    }
  };

  return (
    <Grid container>
      <Grid item md={12}>
        <FormControl fullWidth variant="standard">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              closeOnSelect
              format="dd/MM/yyyy"
              label="From"
              value={date.start}
              onChange={setStartDate}
              disabled={isLoading}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  margin: 'normal',
                  id: 'selectFromLabel',
                  'aria-label': 'change date'
                }
              }}
            />
          </LocalizationProvider>
        </FormControl>
      </Grid>
      <Grid item md={12}>
        <FormControl fullWidth variant="standard">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              format="dd/MM/yyyy"
              label="To"
              value={date.end}
              onChange={setEndDate}
              disabled={isLoading}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  margin: 'normal',
                  id: 'selectToLabel',
                  'aria-label': 'change date'
                }
              }}
            />
          </LocalizationProvider>
        </FormControl>
      </Grid>
      <Grid item md={12} sx={styles.filterContainer}>
        <IconButton aria-describedby={id} onClick={handleClick} disabled={isLoading} sx={V4_ICON_BUTTON}>
          <FilterListOutlined />
        </IconButton>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Grid container sx={styles.popoverContent}>
            <Grid item md={12} sx={styles.popoverGridItem}>
              <FormGroup sx={styles.switchFormGroup}>
                <Typography variant="body1" sx={styles.switchFormGroupPreLabel}>
                  Time
                </Typography>
                <FormControlLabel
                  control={<Switch checked={isAvgCount} onChange={handleChange} color="secondary" />}
                  label="Count"
                />
              </FormGroup>
            </Grid>
            <Grid item md={12}>
              {activities && (
                <ActivityLegend activities={activities} toggleActivity={setActivities} horizontal={false} />
              )}
            </Grid>
          </Grid>
        </Popover>
      </Grid>
      <Grid item md={12} sx={styles.chartContainer}>
        <ReactApexChart options={chart.options} series={chart.series} type="bar" height={500} />
      </Grid>
    </Grid>
  );
};

export default TotalChart;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    fontFamily: {
      fontFamily: theme.typography.fontFamily
    },
    legendCard: {
      paddingBottom: '16px!important',
      display: 'flex',
      justifyContent: 'end'
    },
    detailedListCardContainer: {
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%'
    },
    line: {
      flex: 1,
      borderWidth: 1,
      borderColor: theme.palette.divider,
      borderStyle: 'dashed'
    },
    detailedListCardButtonContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    legendContainer: {
      display: 'flex',
      height: '100%',
      flexDirection: 'column',
      justifyContent: 'space-between'
    },
    filterContainer: {
      display: 'flex',
      justifyContent: 'end'
    },
    switchFormGroup: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center'
    },
    popoverContent: {
      margin: 0,
      padding: theme.spacing(2),
      width: 250
    },
    popoverGridItem: {
      marginBottom: theme.spacing(2)
    },
    switchFormGroupPreLabel: {
      marginRight: theme.spacing(2)
    },
    legendTitle: {
      textAlign: 'center'
    },

    chartContainer: {
      display: 'flex',
      justifyContent: 'center'
    },

    legendFormGroup: {
      paddingLeft: '32px'
    },

    rechartsLabel: {
      fill: '#fff',
      fontWeight: 300,
      fontSize: '12px'
    },

    activityAll: {
      color: theme.palette.text.primary
    },
    activityWalking: {
      color: theme.palette.primary.main
    },
    activityEating: {
      color: '#ff9800' // v4 warning.main
    },
    activityDrinking: {
      color: '#2196f3' // v4 info.main
    },
    activityToilet: {
      color: '#4caf50' // v4 success.main
    },
    activityEnrichment: {
      color: theme.palette.secondary.main
    }
  }) as const;
